import { ReactElement } from 'react';
import { Team } from '@/api';
import {
    UISectionState,
    UIButton,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText,
    useUIConfirmationModal
} from '@/ui';
import { useDeleteTeamAction } from '../actions';
import { useCompaniesTranslation } from '../hooks';
import { useFetchTeamCounts } from '../queries';
import { useNavigate } from 'react-router';

type TeamAdminSettingsSectionProps = { team: Team };

export function TeamAdminSettingsSection(
    props: TeamAdminSettingsSectionProps
): ReactElement {
    const { team } = props;

    const { t } = useCompaniesTranslation();

    // Deleting the last team is blocked, so the section waits for the count
    // rather than deciding without it.
    const { counts, status, refetch } = useFetchTeamCounts(team.companyId);
    const deleteTeamAction = useDeleteTeamAction({
        team,
        teamsCount: counts?.total ?? 0
    });

    const navigate = useNavigate();

    function onDeleteTeam() {
        deleteTeamAction
            .deleteTeam()
            .catch(() => {}) // Error handled in the action
            .finally(() => {
                void navigate(`/company/${team.companyId}/settings/teams`);
            });
    }

    if (!counts) {
        return (
            <UISectionState
                title={t('components.team_admin_settings_section.title')}
                status={status}
                errorText={t(
                    'components.team_admin_settings_section.load_error'
                )}
                onRetry={refetch}
            />
        );
    }

    return (
        <TeamAdminSettingsSectionForm
            loading={deleteTeamAction.loading}
            deleteDisabled={!deleteTeamAction.isAllowed}
            deleteDisabledTooltip={deleteTeamAction.reason}
            onDeleteTeam={onDeleteTeam}
        />
    );
}

type TeamAdminSettingsSectionFormProps = {
    loading: boolean;
    deleteDisabled: boolean;
    deleteDisabledTooltip?: string;
    onDeleteTeam: () => void;
};

function TeamAdminSettingsSectionForm(
    props: TeamAdminSettingsSectionFormProps
): ReactElement {
    const { loading, deleteDisabled, deleteDisabledTooltip, onDeleteTeam } =
        props;

    const { t } = useCompaniesTranslation();

    const confirmationModal = useUIConfirmationModal();

    function _onDeleteTeam() {
        confirmationModal.open({
            title: t(
                'components.team_admin_settings_section.remove_confirm_title'
            ),
            confirmText: t(
                'components.team_admin_settings_section.remove_button'
            ),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                onDeleteTeam();
                confirmationModal.close();
            },
            onCancel: () => {
                confirmationModal.close();
            }
        });
    }

    return (
        <UISection>
            <UISectionHeader
                title={t('components.team_admin_settings_section.title')}
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.team_admin_settings_section.remove_button'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.team_admin_settings_section.remove_description'
                                )}
                            </UIText>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flex: 1
                            }}
                        >
                            <UIButton
                                text={t(
                                    'components.team_admin_settings_section.remove_button'
                                )}
                                type="button"
                                variant="danger"
                                leftIcon="trash-2"
                                onClick={_onDeleteTeam}
                                disabled={deleteDisabled}
                                disabledTooltip={deleteDisabledTooltip}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </UISectionContent>
        </UISection>
    );
}
